import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { DownloadService } from '@treasury/domain/download';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { Feature } from '@treasury/domain/services/feature-flags';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
// import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { WireUploadFormat, WiresService } from '@treasury/domain/wires';
import { circlePlusIcon } from '@treasury/presentation/assets/icons';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/wire-upload-formats-table';

export const tagName = 'wire-files-container';

@customElement(tagName)
export class WireFilesContainer extends TmContainer {
    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    declare private readonly downloadService: DownloadService<any>;

    @InjectProperty()
    declare private readonly wiresService: WiresService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @state()
    private hasWireFileUpload = false;

    @state()
    private wireFileFormats: WireUploadFormat[] = [];

    @state()
    private hasWireFileUploadEntitlement = false;

    @state()
    private tabs = [{ label: 'Wire File Formats', disabled: false, id: 'wire-file-formats' }];

    @state()
    private activeTab = 'wire-file-formats';

    public connectedCallback(): void {
        super.connectedCallback();
        this.headerService.configure({
            title: 'Wire Upload',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.WireFiles}
                        .disabled=${true}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
            backAction: () => {
                this.navService.navigate('/wires');
            },
        });
    }

    public disconnectedCallback() {
        super.disconnectedCallback?.();
        this.footerService.hide();
    }

    protected render() {
        return html`${this.renderActionItems()}<tm-section
                class=${classMap({
                    'flex flex-col': true,
                    'overflow-hidden h-full bg-page': !this.isDesktop,
                    'mx-2 mb-4 mt-2 py-2 rounded-lg': this.isDesktop,
                })}
                >${this.loading
                    ? html`<tm-loader card></tm-loader>`
                    : html`<div
                              class=${classMap({
                                  'items-center border-b flex justify-between': true,
                                  'bg-primary': !this.isDesktop,
                              })}
                          >
                              <tm-tabs
                                  .tabs=${this.tabs}
                                  .activeTab=${this.activeTab}
                                  .border=${false}
                                  @switchTab=${(e: CustomEvent) => {
                                      this.activeTab = e.detail.activeTab;
                                  }}
                              ></tm-tabs>
                              ${this.isDesktop
                                  ? html`<tm-button
                                        importance="primary"
                                        .icon=${circlePlusIcon}
                                        class="mr-4"
                                        @click=${() => {
                                            this.navService.navigate(`wires/files/new-format`);
                                        }}
                                        >Create new format</tm-button
                                    >`
                                  : nothing}
                          </div>
                          <wire-upload-formats-table
                              .wireFileFormats=${this.wireFileFormats}
                              @wire-file-selected=${(e: CustomEvent) => {
                                  const format: WireUploadFormat = e.detail.wireFile;
                                  this.navService.navigate(`wires/files/${format.id}`);
                              }}
                              @wire-upload-format-deleted=${() => {
                                  this.fetchWireFileFormats();
                              }}
                          ></wire-upload-formats-table>`}</tm-section
            >`;
    }

    protected renderActionItems() {
        return this.hasWireFileUploadEntitlement
            ? html`<tm-action-button
                  class="my-2"
                  .buttonTitle=${'Upload Wire File'}
                  @click=${() => {
                      this.navService.navigate(`/wires/upload`);
                  }}
              ></tm-action-button>`
            : nothing;
    }

    protected async firstUpdated() {
        this.hasWireFileUpload = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaWireFileUpload);
        this.hasWireFileUploadEntitlement = await this.entitlementsService.isEnabled(
            Entitlements.WireUpload
        );
        await this.fetchWireFileFormats();
    }

    private async fetchWireFileFormats() {
        this.loading = true;
        this.wireFileFormats = await this.wiresService.fetchWireUploadFormats();
        this.loading = false;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireFilesContainer;
    }
}
